import { cn } from "../../../lib/cn";
import { Line } from "../../Line";
import { Heading } from "../../typography/Heading";
import { TaggedArticle } from "./articles/Articles";
import { ConfirmedBookingCard } from "./bookings/ConfirmedBooking";
import { ProposalCard } from "./bookings/Proposal";
import { useMessage } from "./Context";
import { MessageReply } from "./Reply";
import { NominationDetails } from "./requests/Nomination";
import { QuickRequestCard } from "./requests/QuickRequest";
import type {
    ChatArticle,
    ChatAttachment,
    ChatMessageReply,
    ChatSender,
    ConfirmedBooking,
    Nomination,
    Proposal,
    QuickRequest,
} from "./types";
import { useIsTouchDevice } from "./useIsTouchDevice";

const chatMessageTextClass =
    "break-words [hyphens:auto] [-webkit-hyphens:auto] whitespace-pre-wrap font-weight-paragraph-regular font-paragraph-base text-mobile-chat-medium leading-mobile-chat-medium [&_a]:cursor-pointer [&_a]:underline";

export function MessageContent() {
    return (
        <div className="grid w-full grid-cols-1 gap-16">
            <QuickRequestCard />

            <ProposalContent />

            <MessageDetails />
        </div>
    );
}

export function MessageContentWithoutProposal() {
    return (
        <div className="grid w-full grid-cols-1 gap-16">
            <MessageDetails />
        </div>
    );
}

function MessageDetails() {
    return (
        <>
            <TaggedArticleContent />

            <ConfirmedBookingsContent />

            <MessageReply />

            <NominationHeading />

            <MessageText />

            <NominationDetails />
        </>
    );
}

function ProposalContent() {
    const { proposal } = useMessage();

    if (!proposal) {
        return null;
    }

    return <ProposalCard proposal={proposal} />;
}

function TaggedArticleContent() {
    const { sender, proposal, articles, message, messageHtml } = useMessage();
    const article = articles[0];

    if (proposal || sender !== "member" || !article) {
        return null;
    }

    const hasMessage = hasMessageContent(message, messageHtml);

    return (
        <>
            <TaggedArticle article={article} />

            {hasMessage && <Line variant="dashed" />}
        </>
    );
}

function ConfirmedBookingsContent() {
    const { confirmedBookings, message, messageHtml } = useMessage();

    if (confirmedBookings.length === 0) {
        return null;
    }

    const hasMessage = hasMessageContent(message, messageHtml);

    return (
        <>
            <div className="flex w-full flex-col items-start gap-8">
                {confirmedBookings.map((booking) => (
                    <ConfirmedBookingCard key={booking.id} booking={booking} />
                ))}
            </div>

            {hasMessage && <Line variant="dashed" />}
        </>
    );
}

function NominationHeading() {
    const { nomination } = useMessage();

    if (!nomination) {
        return null;
    }

    return (
        <>
            <Heading level={5}>Concierge nomination</Heading>

            <Line variant="dashed" />
        </>
    );
}

function MessageText() {
    const { message, messageHtml } = useMessage();
    const isTouchDevice = useIsTouchDevice();

    if (messageHtml && messageHtml !== "<span></span>") {
        return (
            <p
                className={cn(chatMessageTextClass, !isTouchDevice && "select-text")}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: static deterministic fixture HTML, parity with shared-web ChatMessage
                dangerouslySetInnerHTML={{ __html: messageHtml }}
            />
        );
    }

    if (!message) {
        return null;
    }

    return <p className={chatMessageTextClass}>{message}</p>;
}

export function hasMessageContent(
    message: string | undefined,
    messageHtml: string | undefined,
) {
    return Boolean(message || (messageHtml && messageHtml !== "<span></span>"));
}

export function hasBubbleContent({
    sender,
    message,
    messageHtml,
    proposal,
    reply,
    quickRequest,
    nomination,
    confirmedBookings,
    articles,
}: {
    sender: ChatSender;
    message?: string;
    messageHtml?: string;
    proposal?: Proposal;
    reply?: ChatMessageReply;
    quickRequest?: QuickRequest;
    nomination?: Nomination;
    confirmedBookings: ConfirmedBooking[];
    articles: ChatArticle[];
}) {
    return (
        hasMessageContent(message, messageHtml) ||
        Boolean(proposal || reply || quickRequest || nomination) ||
        confirmedBookings.length > 0 ||
        (sender === "member" && articles.length > 0)
    );
}

export function getMessagePlainText(
    message: string | undefined,
    messageHtml: string | undefined,
) {
    if (message) {
        return message;
    }

    if (!messageHtml || messageHtml === "<span></span>") {
        return "";
    }

    const parsed = new DOMParser().parseFromString(messageHtml, "text/html");
    return parsed.body.textContent?.trim() ?? "";
}

export function getReplyPreviewText({
    message,
    messageHtml,
    attachments,
    imageLabel,
    audioLabel,
    fileFallbackLabel,
}: {
    message?: string;
    messageHtml?: string;
    attachments: ChatAttachment[];
    imageLabel: string;
    audioLabel: string;
    fileFallbackLabel: string;
}) {
    const plain = getMessagePlainText(message, messageHtml);
    if (plain) {
        return plain;
    }

    if (attachments.some((attachment) => attachment.mimeType.startsWith("image/"))) {
        return imageLabel;
    }

    if (attachments.some((attachment) => attachment.mimeType.startsWith("audio/"))) {
        return audioLabel;
    }

    const file = attachments.find(
        (attachment) =>
            !attachment.mimeType.startsWith("image/") &&
            !attachment.mimeType.startsWith("audio/"),
    );
    if (file) {
        return file.filename ?? fileFallbackLabel;
    }

    return "";
}
